import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import type { Client } from '@src/clients/domain/Client';
import { useCreateClientUser } from '@src/clientUser/manage/add/useCreateClientUser';
import {
  ClientUserEmailField,
  ClientUserNameField,
  ClientUserPhoneField,
} from '@src/clientUser/manage/ClientUserFormInputs';
import { BaseModal } from '@src/common/components/BaseModal';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useTranslation } from 'react-i18next';

export const AddClientUserModal = ({
  client,
  open,
  onClose,
  onCreated,
}: {
  client: Client;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();

  const { errors, register, submit, reset } = useCreateClientUser({
    client,
    onSuccess: () => {
      showSuccessToast(
        t('clientUsers.addModal.onSuccess', { clientName: client.name }),
      );
      onCreated();
      reset();
    },
    onError: () =>
      showErrorToast(
        t('clientUsers.addModal.onError', { clientName: client.name }),
      ),
  });

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t('clientUsers.addModal.title', { clientName: client.name })}
      titleIcon={<PersonAddOutlined />}
    >
      <Stack gap={theme.spacing['3']}>
        <TextInput
          label={t('name')}
          placeholder={t('clientUsers.fields.name.placeholder')}
          error={
            errors[ClientUserNameField.fieldName] &&
            t(`formErrors.${errors[ClientUserNameField.fieldName]!.message}`)
          }
          {...register(ClientUserNameField.fieldName)}
        />

        <TextInput
          label={t('phone')}
          placeholder={t('clientUsers.fields.phone.placeholder')}
          error={
            errors[ClientUserPhoneField.fieldName] &&
            t(`formErrors.${errors[ClientUserPhoneField.fieldName]!.message}`)
          }
          {...register(ClientUserPhoneField.fieldName)}
        />

        <TextInput
          label={t('email')}
          placeholder={t('clientUsers.fields.email.placeholder')}
          error={
            errors[ClientUserEmailField.fieldName] &&
            t(`formErrors.${errors[ClientUserEmailField.fieldName]!.message}`)
          }
          {...register(ClientUserEmailField.fieldName)}
        />

        <CloseConfirmModalFooter
          onClose={() => {
            onClose();
            reset();
          }}
          confirm={submit}
          confirmLabel={t('create')}
        />
      </Stack>
    </BaseModal>
  );
};
